import { useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { usePlatformModules } from '@/hooks/use-modules';
import { apiClient } from '@/lib/api-client';
import { precacheSafetyDocuments } from '@/hooks/use-offline';

const SAFETY_API = '/api/v1/safety';

/**
 * On login, fetch the most recent safety documents (JSAs, Safe Work Plans,
 * Rescue Plans) and tell the service worker to pre-cache them for offline access.
 *
 * This hook should be called once in the authenticated app shell.
 */
export function useSafetyDocPrecache() {
  const { isAuthenticated } = useAuth();
  const { isModuleEnabled } = usePlatformModules();
  const safetyEnabled = isModuleEnabled('safety');

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!safetyEnabled) return;
    if (!navigator.serviceWorker?.controller) return;

    const precache = async () => {
      try {
        const urls: string[] = [];

        // Fetch the most recent JSAs
        const jsaRes = await apiClient.get(`${SAFETY_API}/jsas/`, {
          params: { page_size: 10, ordering: '-created_at' },
        });
        if (jsaRes.data?.results) {
          for (const jsa of jsaRes.data.results) {
            urls.push(`${SAFETY_API}/jsas/${jsa.id}/`);
          }
        }

        // Fetch the most recent Safe Work Plans
        const swpRes = await apiClient.get(`${SAFETY_API}/work-plans/`, {
          params: { page_size: 10, ordering: '-created_at' },
        });
        if (swpRes.data?.results) {
          for (const swp of swpRes.data.results) {
            urls.push(`${SAFETY_API}/work-plans/${swp.id}/`);
          }
        }

        // Fetch the most recent Rescue Plans
        const rpRes = await apiClient.get(`${SAFETY_API}/rescue-plans/`, {
          params: { page_size: 10, ordering: '-created_at' },
        });
        if (rpRes.data?.results) {
          for (const rp of rpRes.data.results) {
            urls.push(`${SAFETY_API}/rescue-plans/${rp.id}/`);
          }
        }

        if (urls.length > 0) {
          // Tokens are now in httpOnly cookies — the SW's fetch uses
          // credentials: 'include' so cookies are sent automatically.
          precacheSafetyDocuments(urls);
        }
      } catch {
        // Silently fail — pre-caching is best-effort
      }
    };

    // Delay slightly to avoid competing with initial page load
    const timer = setTimeout(precache, 5000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, safetyEnabled]);
}
